import React, { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { startOfYear, endOfYear, subSeconds, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { getCalendarEvents, updateDate as apiUpdateDate, createDate as apiCreateDate } from '../services/calendarApi';
import { useAuth } from './AuthContext';
import { useResources } from './ResourceContext';
import { deleteResource } from '../services/resourceApi';
import { loadRRule } from '../utils/rruleHelper';

export const CalendarContext = createContext(null);

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};

export const CalendarProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { resources, isLoading: isLoadingResources, error: resourceError, upsertResource, refreshResources } = useResources();
  
  const [dates, setDates] = useState([]);
  const [loadedRanges, setLoadedRanges] = useState([]); // Array of { start: number, end: number }
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [datesError, setDatesError] = useState(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [calendarToDelete, setCalendarToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Helper to merge contiguous ranges
  const mergeRanges = (ranges) => {
    if (ranges.length === 0) return [];
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    const merged = [];
    let current = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      // Allow 1 second tolerance for continuity
      if (current.end >= next.start - 1000) {
        current.end = Math.max(current.end, next.end);
      } else {
        merged.push(current);
        current = next;
      }
    }
    merged.push(current);
    return merged;
  };

  /**
   * Ensures that events for the given range are loaded.
   * Fetches only what is missing from the loadedRanges cache.
   */
  const ensureDateRange = useCallback(async (start, end) => {
    if (!isAuthenticated) return;
    const startMs = start.getTime();
    const endMs = end.getTime();

    // Check if fully covered by any existing range
    const isCovered = loadedRanges.some(r => r.start <= startMs && r.end >= endMs);
    if (isCovered) return;

    // Simple optimization: If request overlaps significantly with an existing range,
    // only fetch the new segment. For complex fragmentation, we fetch the whole requested chunk
    // to ensure consistency, relying on deduplication below.
    let fetchStart = start;
    let fetchEnd = end;

    // Sort ranges to check boundaries
    const sortedRanges = [...loadedRanges].sort((a, b) => a.start - b.start);
    
    for (const r of sortedRanges) {
        // If requested range overlaps the end of a loaded range (scrolling forward)
        if (r.start <= startMs && r.end > startMs && r.end < endMs) {
            fetchStart = new Date(r.end);
        }
        // If requested range overlaps the start of a loaded range (scrolling backward)
        if (r.start > startMs && r.start <= endMs && r.end >= endMs) {
            fetchEnd = new Date(r.start);
        }
    }

    if (fetchStart >= fetchEnd) return;

    setIsLoadingDates(true);
    try {
      console.log(`[CalendarContext] Fetching dates from ${fetchStart.toISOString()} to ${fetchEnd.toISOString()}`);
      const fetchedDates = await getCalendarEvents(fetchStart, fetchEnd);
      
      setDates(prev => {
        const existingIds = new Set(prev.map(d => d.id));
        const newDates = [...prev];
        fetchedDates.forEach(d => {
            if (!existingIds.has(d.id)) {
                existingIds.add(d.id);
                newDates.push(d);
            } else {
                // Update existing event with fresh data
                const idx = newDates.findIndex(x => x.id === d.id);
                if (idx !== -1) newDates[idx] = d;
            }
        });
        return newDates;
      });

      setLoadedRanges(prev => {
        const newRange = { start: fetchStart.getTime(), end: fetchEnd.getTime() };
        return mergeRanges([...prev, newRange]);
      });
    } catch (err) {
      console.error("Error fetching date range:", err);
      setDatesError(err.message);
    } finally {
      setIsLoadingDates(false);
    }
  }, [isAuthenticated, loadedRanges]);

  // Refreshes data for all currently loaded ranges
  const loadDates = useCallback(async () => {
    if (!isAuthenticated) {
      setDates([]);
      setLoadedRanges([]);
      return;
    }
    
    // If no ranges loaded yet (initial load), fetch current year as baseline for sidebar
    if (loadedRanges.length === 0) {
        const now = new Date();
        await ensureDateRange(startOfYear(now), endOfYear(now));
        return;
    }

    setIsLoadingDates(true);
    setDatesError(null);
    try {
      // Re-fetch all loaded ranges in parallel
      const promises = loadedRanges.map(r => getCalendarEvents(new Date(r.start), new Date(r.end)));
      const results = await Promise.all(promises);
      const allEvents = results.flat();
      
      // Deduplicate
      const uniqueEvents = [];
      const ids = new Set();
      allEvents.forEach(e => {
          if (!ids.has(e.id)) {
              ids.add(e.id);
              uniqueEvents.push(e);
          }
      });
      setDates(uniqueEvents);
    } catch (err) {
      console.error("Error refreshing dates:", err);
      setDatesError(err.message || "Failed to load calendar dates.");
    } finally {
      setIsLoadingDates(false);
    }
  }, [isAuthenticated, loadedRanges, ensureDateRange]);

  useEffect(() => {
    if (isAuthenticated) {
      loadDates();
    } else {
      setDates([]);
      setLoadedRanges([]);
      setDatesError(null);
    }
  }, [isAuthenticated]); // Removed loadDates from dep array to prevent loop if it changes

  const addDate = useCallback(async (newDateData) => {
    try {
      const created = await apiCreateDate(newDateData);
      upsertResource({ ...created, type: 'date', resource_type: 'date' });
      await loadDates();
    } catch (error) {
      console.error("Failed to create date:", error);
      throw error;
    }
  }, [loadDates, upsertResource]);

  const updateDate = useCallback(async (eventId, dateData, editScope = 'series', originalInstanceStart = null) => {
    try {
      const { RRule, RRuleSet, rrulestr } = await loadRRule();
      const originalSeriesEvent = dates.find(d => d.id === eventId);
      if (!originalSeriesEvent) {
        throw new Error(`[CalendarContext] Original series event with ID ${eventId} not found in context.`);
      }
      const originalRruleString = originalSeriesEvent.rrule || originalSeriesEvent.metadata?.source_rrule;
      let updatedResource = null;

      switch (editScope) {
        case 'instance': {
          console.log(`[CalendarContext] Splitting instance for series ${eventId}. Original start: ${originalInstanceStart}`);
          if (originalRruleString) {
            const rruleSet = new RRuleSet();
            rruleSet.rrule(rrulestr(originalRruleString));
            rruleSet.exdate(new Date(originalInstanceStart));
            const updatedSeries = await apiUpdateDate(eventId, { rrule: rruleSet.toString() });
            upsertResource({ ...updatedSeries, type: 'date', resource_type: 'date' });
          }
          const newMetadata = {};
          if (dateData.metadata?.calendarId) {
            newMetadata.calendarId = dateData.metadata.calendarId;
          }
          const newSingleEventData = {
            ...dateData,
            rrule: null,
            metadata: newMetadata
          };
          const createdInstance = await apiCreateDate(newSingleEventData);
          upsertResource({ ...createdInstance, type: 'date', resource_type: 'date' });
          break;
        }
        case 'future': {
          console.log(`[CalendarContext] Splicing series ${eventId} from date: ${originalInstanceStart}`);
          const splitDate = new Date(originalInstanceStart);
          if (originalRruleString) {
            const originalRuleObject = rrulestr(originalRruleString);
            const baseRule = (originalRuleObject instanceof RRuleSet) ? originalRuleObject.rrules()[0] : originalRuleObject;
            if (!baseRule) throw new Error("Could not find a base RRULE to modify.");
            
            const originalOptions = baseRule.options;
            const truncatedRule = new RRule({ ...originalOptions, until: subSeconds(splitDate, 1) });
            
            const updatedSeries = await apiUpdateDate(eventId, { rrule: truncatedRule.toString() });
            upsertResource({ ...updatedSeries, type: 'date', resource_type: 'date' });

            const newSeriesOptions = { ...originalOptions };
            newSeriesOptions.dtstart = dateData.start_datetime;
            
            if (newSeriesOptions.freq === RRule.WEEKLY) {
                const newDay = dateData.start_datetime.getDay();
                const rruleWeekday = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA][newDay];
                newSeriesOptions.byweekday = [rruleWeekday];
            }
            if (originalOptions.count) {
                const allOriginalDates = baseRule.all();
                const splitIndex = allOriginalDates.findIndex(d => d.getTime() === splitDate.getTime());
                if (splitIndex !== -1) {
                    newSeriesOptions.count = allOriginalDates.length - splitIndex;
                    delete newSeriesOptions.until;
                }
            }

            const newSeriesRule = new RRule(newSeriesOptions);
            const newSeriesData = { ...dateData, rrule: newSeriesRule.toString() };
            const createdSeries = await apiCreateDate(newSeriesData);
            upsertResource({ ...createdSeries, type: 'date', resource_type: 'date' });
          }
          break;
        }
        case 'series':
        default: {
          console.log(`[CalendarContext] Updating entire series ${eventId}`);
          if (originalRruleString) {
            const originalRuleObject = rrulestr(originalRruleString);
            let originalOptions;
            let originalExdates = [];
            let originalRdates = [];
            let originalExrules = [];

            if (originalRuleObject instanceof RRuleSet) {
              const baseRule = originalRuleObject.rrules()[0];
              if (!baseRule) throw new Error("Cannot modify series: RRuleSet has no base RRULE.");
              originalOptions = baseRule.options;
              originalExdates = originalRuleObject.exdates();
              originalRdates = originalRuleObject.rdates();
              originalExrules = originalRuleObject.exrules();
            } else {
              originalOptions = originalRuleObject.options;
            }

            const originalSeriesStartDate = originalOptions.dtstart;
            const draggedInstanceStartDate = new Date(originalInstanceStart || originalSeriesStartDate);
            const droppedInstanceStartDate = dateData.start_datetime;
            
            const timeShift = droppedInstanceStartDate.getTime() - draggedInstanceStartDate.getTime();
            const newSeriesStartDate = new Date(originalSeriesStartDate.getTime() + timeShift);

            const newRuleOptions = { ...originalOptions, dtstart: newSeriesStartDate };
            
            if (newRuleOptions.freq === RRule.WEEKLY) {
                const newDay = newSeriesStartDate.getDay();
                const rruleWeekday = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA][newDay];
                newRuleOptions.byweekday = [rruleWeekday];
            }
            if (newRuleOptions.until) {
                newRuleOptions.until = new Date(newRuleOptions.until.getTime() + timeShift);
            }

            const newBaseRule = new RRule(newRuleOptions);
            const newRruleSet = new RRuleSet();
            newRruleSet.rrule(newBaseRule);

            originalExdates.forEach(exdate => {
              const newExdate = new Date(exdate.getTime() + timeShift);
              newRruleSet.exdate(newExdate);
            });
            originalRdates.forEach(rdate => {
              const newRdate = new Date(rdate.getTime() + timeShift);
              newRruleSet.rdate(newRdate);
            });
            originalExrules.forEach(rule => newRruleSet.exrule(rule));

            const duration = new Date(originalSeriesEvent.end_datetime).getTime() - new Date(originalSeriesEvent.start_datetime).getTime();
            
            const payload = {
                ...dateData,
                start_datetime: newSeriesStartDate,
                end_datetime: new Date(newSeriesStartDate.getTime() + duration),
                rrule: newRruleSet.toString(),
            };
            updatedResource = await apiUpdateDate(eventId, payload);
          } else {
            updatedResource = await apiUpdateDate(eventId, dateData);
          }
          if (updatedResource) {
             upsertResource({ ...updatedResource, type: 'date', resource_type: 'date' });
          }
          break;
        }
      }
      await loadDates();
    } catch (error) {
      console.error(`[CalendarContext] Failed to update date (scope: ${editScope}):`, error);
      throw error;
    }
  }, [dates, loadDates, upsertResource]);

  const deleteDate = useCallback(async (eventToDelete, deleteScope) => {
    try {
      const { RRule, RRuleSet, rrulestr } = await loadRRule();
      const isRecurring = !!(eventToDelete.rrule || eventToDelete.metadata?.source_rrule);

      if (!isRecurring) {
        await deleteResource('date', eventToDelete.id);
      } else {
        const originalSeriesEvent = dates.find(d => d.id === eventToDelete.id);
        if (!originalSeriesEvent) {
          throw new Error(`Cannot delete recurring event: Original series with ID ${eventToDelete.id} not found.`);
        }
        const originalRruleString = originalSeriesEvent.rrule || eventToDelete.metadata.source_rrule;
        const instanceDate = eventToDelete.instanceStart 
          ? new Date(eventToDelete.instanceStart) 
          : new Date(eventToDelete.start_datetime);

        switch (deleteScope) {
          case 'instance': {
            const rruleSet = new RRuleSet();
            rruleSet.rrule(rrulestr(originalRruleString));
            rruleSet.exdate(instanceDate);
            
            const newRruleString = rruleSet.toString();
            const payload = { rrule: newRruleString };
            const updated = await apiUpdateDate(eventToDelete.id, payload);
            upsertResource({ ...updated, type: 'date', resource_type: 'date' });
            break;
          }
          case 'future': {
            const originalRuleObject = rrulestr(originalRruleString);
            let originalOptions;
            if (originalRuleObject instanceof RRuleSet) {
              const baseRule = originalRuleObject.rrules()[0];
              if (!baseRule) {
                throw new Error("Cannot truncate series: The RRuleSet does not contain a base RRULE.");
              }
              originalOptions = baseRule.options;
            } else {
              originalOptions = originalRuleObject.options;
            }

            const newRule = new RRule({
              ...originalOptions,
              until: subSeconds(instanceDate, 1),
            });
            const newRruleString = newRule.toString();
            console.log(`[CalendarContext] Truncating series ${eventToDelete.id} to end before ${instanceDate.toISOString()}. New rule: ${newRruleString}`);
            
            const payload = { rrule: newRruleString };
            const updated = await apiUpdateDate(eventToDelete.id, payload);
            upsertResource({ ...updated, type: 'date', resource_type: 'date' });
            break;
          }
          case 'series': {
            console.log(`[CalendarContext] Deleting entire event/series ${eventToDelete.id}`);
            await deleteResource('date', eventToDelete.id);
            break;
          }
          default:
            throw new Error(`Unknown delete scope: ${deleteScope}`);
        }
      }
      await refreshResources();
      await loadDates();
    } catch (error) {
      console.error(`[CalendarContext] Failed to delete date:`, error);
      throw error;
    }
  }, [dates, loadDates, upsertResource, refreshResources]);

  const moveDateToCalendar = useCallback(async (dateId, newCalendarId) => {
    const dateToMove = dates.find(d => d.id === dateId);
    if (!dateToMove) {
      console.error(`[CalendarContext] Cannot move date ${dateId}: not found.`);
      return;
    }
    const currentCalendarId = dateToMove.metadata?.calendarId || null;
    if (currentCalendarId === newCalendarId) return;

    const newMetadata = { ...dateToMove.metadata };
    if (newCalendarId) {
      newMetadata.calendarId = newCalendarId;
    } else {
      delete newMetadata.calendarId;
    }

    try {
      const updated = await apiUpdateDate(dateId, { metadata: newMetadata });
      upsertResource({ ...updated, type: 'date', resource_type: 'date' });
      await loadDates();
    } catch (error) {
      console.error(`[CalendarContext] Failed to move date ${dateId}:`, error);
      setDatesError(`Failed to move event: ${error.message}`);
      throw error;
    }
  }, [dates, loadDates, upsertResource]);

  const { calendars, orphanedDates } = useMemo(() => {
    if (!resources || !dates) return { calendars: [], orphanedDates: [] };
    const calendarResources = resources.filter(r => r.type === 'calendar');
    const calendarIdSet = new Set(calendarResources.map(c => c.id));

    const processedCalendars = calendarResources.map(calResource => ({
      id: calResource.id,
      name: calResource.title,
      color: calResource.metadata?.color || '#757575',
      dates: dates.filter(d => d.metadata?.calendarId === calResource.id)
    }));

    const processedOrphanedDates = dates.filter(d => !d.metadata?.calendarId || !calendarIdSet.has(d.metadata.calendarId));
    return { calendars: processedCalendars, orphanedDates: processedOrphanedDates };
  }, [resources, dates]);

  const openDeleteDialog = useCallback((calendar) => {
    setCalendarToDelete(calendar);
    setDeleteError(null);
    setIsDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setTimeout(() => {
      if (!isDeleteDialogOpen) {
        setCalendarToDelete(null);
        setDeleteError(null);
      }
    }, 300);
  }, [isDeleteDialogOpen]);

  const confirmDeleteCalendar = useCallback(async (mode) => {
    if (!calendarToDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      if (mode === 'recursive') {
        const datesToDelete = dates.filter(d => d.metadata?.calendarId === calendarToDelete.id);
        for (const date of datesToDelete) {
          await deleteResource('date', date.id);
        }
      }
      await deleteResource('collection', calendarToDelete.id);
      closeDeleteDialog();
      await refreshResources();
      await loadDates();
    } catch (err) {
      console.error(`[CalendarContext] Error during calendar deletion (mode: ${mode}):`, err);
      setDeleteError(err.message || 'Failed to delete calendar.');
    } finally {
      setIsDeleting(false);
    }
  }, [calendarToDelete, dates, closeDeleteDialog, refreshResources, loadDates]);

  const isLoading = isLoadingResources || isLoadingDates;
  const error = resourceError || datesError;

  const contextValue = useMemo(() => ({
    dates,
    calendars,
    orphanedDates,
    isLoading,
    error,
    loadDates,
    ensureDateRange,
    addDate,
    updateDate,
    deleteDate,
    moveDateToCalendar,
    isDeleteDialogOpen,
    calendarToDelete,
    isDeleting,
    deleteError,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDeleteCalendar,
  }), [
    dates, calendars, orphanedDates, isLoading, error, loadDates, ensureDateRange, addDate, updateDate, deleteDate,
    moveDateToCalendar, isDeleteDialogOpen, calendarToDelete, isDeleting, deleteError,
    openDeleteDialog, closeDeleteDialog, confirmDeleteCalendar
  ]);

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
};

CalendarProvider.propTypes = {
  children: PropTypes.node.isRequired,
};